// Route tree (IA §1, §2.1, §2.6). Store-scoped pages live under /{org}/{store}/…, org-scoped
// settings under /{org}/settings/…. Everything below the "app" layout requires a session.
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { t } from "@ady/i18n";
import { CrashPanel, ErrorState, isChunkLoadError, StatePanel } from "@ady/ui";
import { useMe } from "./api/queries";
import { isUnauthenticated } from "./api/errors";
import { MfaPage } from "./auth/Mfa";
import { OnboardingPage } from "./onboarding/Onboarding";
import { SecurityPage, SessionsPage } from "./auth/Security";
import { SignInPage } from "./auth/SignIn";
import { VerifyPage } from "./auth/Verify";
import { AcceptInvitationPage } from "./invitations/AcceptInvitation";
import { PlaceholderPage } from "./pages/Placeholder";
import { TodayPage } from "./pages/Today";
import { BillingPage } from "./settings/Billing";
import { TeamPage } from "./settings/Team";
import { AppLayout } from "./shell/AppShell";
import { catalogRoutes } from "./modules/catalog";
import { commerceRoutes } from "./modules/commerce";
import { operationsRoutes } from "./modules/operations";
import { storefrontRoutes } from "./modules/storefront";
import { paymentsRoutes } from "./modules/payments";
import { fulfilmentRoutes } from "./modules/fulfilment";
import { integrationsRoutes } from "./modules/integrations";
import { importsRoutes } from "./modules/imports";
import { walletRoutes } from "./modules/wallet";
import { notificationsRoutes } from "./modules/notifications";
import { analyticsRoutes } from "./modules/analytics";
import { engagementRoutes } from "./modules/engagement";
import { automationRoutes } from "./modules/automation";
import { marketingRoutes } from "./modules/marketing";
import { headlessRoutes } from "./modules/headless";
import { settingsRoutes } from "./modules/settings";
import { useGo } from "./shell/AppLink";
import { hubRoutes } from "./shell/GroupHub";
import { defaultStore } from "./shell/context";
import { GoImport, takeResume } from "./services/GoImport";

function HomeRedirect() {
  const me = useMe();
  const go = useGo();
  useEffect(() => {
    if (me.isError && isUnauthenticated(me.error)) go("/sign-in");
    else if (me.data) {
      // Signed in after the Import Helper extension sent the vendor to sign-in: continue there.
      const resume = takeResume();
      if (resume) return go(resume);
      const d = defaultStore(me.data);
      // No store yet: business setup (IA §6.1), which also resumes a half-finished setup.
      go(d ? `/${d.org}/${d.store}/today` : "/onboarding");
    }
  }, [me.isError, me.error, me.data, go]);
  return null;
}

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => <StatePanel kind="error" size="page" title={t("errors.notFound")} />,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRedirect,
});
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignInPage,
});
const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in/verify",
  component: VerifyPage,
});
// Import Helper extension: Order → Import Marketplace's India / China section (sign-in first if needed).
const goImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/go/import/$site",
  component: GoImport,
});
// AI-07 MCP sign-in (D-090): an AI app asks to connect; the person approves (signs in first if needed).
const connectAuthorizeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connect/authorize",
  component: lazyRouteComponent(
    () => import("./integrations/ConnectAuthorize"),
    "ConnectAuthorizePage",
  ),
});
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: OnboardingPage,
});
const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in/mfa",
  component: MfaPage,
});

const appRoute = createRoute({ getParentRoute: () => rootRoute, id: "app", component: AppLayout });
const securityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/account/security",
  component: SecurityPage,
});
const todayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/$org/$store/today",
  component: TodayPage,
});
const sessionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/account/sessions",
  component: SessionsPage,
});
const teamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/$org/settings/team",
  component: TeamPage,
});
const billingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/$org/settings/billing",
  component: BillingPage,
});
const orgSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/$org/settings/$",
  component: () => <PlaceholderPage scope="org" />,
});
// Outside the shell: the invitee has no membership in the inviting business yet.
const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitations/$token",
  component: AcceptInvitationPage,
});
const storeAreaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/$org/$store/$",
  // The store's own address (nothing after it) opens its Home; it showed "We couldn't find that"
  // (D-106). The splat route also answers the bare /{org}/{store}, so the redirect lives here.
  beforeLoad: ({ params }) => {
    if (!params._splat?.replace(/\/+$/, ""))
      throw redirect({
        to: "/$org/$store/today",
        params: { org: params.org, store: params.store },
        replace: true,
      });
  },
  component: () => <PlaceholderPage scope="store" />,
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  signInRoute,
  verifyRoute,
  mfaRoute,
  onboardingRoute,
  goImportRoute,
  connectAuthorizeRoute,
  invitationRoute,
  appRoute.addChildren([
    securityRoute,
    sessionsRoute,
    todayRoute,
    teamRoute,
    billingRoute,
    // Phase 5 module routes (each module owns its file in src/modules/); before the placeholders.
    ...catalogRoutes(appRoute),
    ...storefrontRoutes(appRoute),
    ...commerceRoutes(appRoute),
    ...operationsRoutes(appRoute),
    // Phase 6 module routes.
    ...paymentsRoutes(appRoute),
    ...fulfilmentRoutes(appRoute),
    ...integrationsRoutes(appRoute),
    ...importsRoutes(appRoute),
    // Phase 7a module routes.
    ...walletRoutes(appRoute),
    ...notificationsRoutes(appRoute),
    ...analyticsRoutes(appRoute),
    ...engagementRoutes(appRoute),
    ...automationRoutes(appRoute),
    ...marketingRoutes(appRoute),
    ...headlessRoutes(appRoute),
    ...settingsRoutes(appRoute),
    // Online store, Apps and Settings overviews (card grids).
    ...hubRoutes(appRoute),
    orgSettingsRoute,
    storeAreaRoute,
  ]),
]);

// Any render or loader failure shows the design-system error state (never the library's raw
// error text). Details stay in the console for debugging; Retry re-runs the route.
function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  useEffect(() => console.error(error), [error]);
  // A screen whose code file is gone after a deploy asks for a reload (retrying would fail again).
  if (isChunkLoadError(error)) return <CrashPanel size="page" error={error} />;
  return <ErrorState size="page" body={t("errors.generic")} onRetry={reset} />;
}

export const router = createRouter({
  routeTree,
  defaultPreload: false,
  scrollRestoration: true,
  defaultErrorComponent: RouteError,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
